import { useCallback, useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import useEmblaCarousel from "embla-carousel-react";
import { ChevronLeft, ChevronRight, Download, X } from "lucide-react";
import Navigation from "@/components/Navigation";
import { Footer } from "@/components/Footer";
import { LocalizedLink } from "@/components/LocalizedLink";
import { useI18n } from "@/i18n";

const TOTAL = 36;
const pages = Array.from({ length: TOTAL }, (_, i) => `/images/revista-2027/page-${String(i + 1).padStart(2, "0")}.webp`);

const copy = {
  en: { label: "Magazine", open: "Tap to open", close: "Close book", pdf: "Download PDF", prev: "Previous page", next: "Next page",
    lead: "The REM7 Collection 2027 digital magazine. Swipe, use the arrows or your keyboard to turn the pages.",
    page: "page", contact: "Enquiries" },
  es: { label: "Revista", open: "Toca para abrir", close: "Cerrar libro", pdf: "Descargar PDF", prev: "Página anterior", next: "Página siguiente",
    lead: "La revista digital REM7 Collection 2027. Desliza, usa las flechas o el teclado para pasar las páginas.",
    page: "página", contact: "Consultas" },
};

const fontCss = `
@font-face{font-family:"RevistaSerif";src:url(/images/revista-2027/serif.woff) format("woff");font-display:swap}
@font-face{font-family:"RevistaSans";src:url(/images/revista-2027/sans.woff) format("woff");font-display:swap}
.rv-serif{font-family:"RevistaSerif","Playfair Display",serif}
.rv-sans{font-family:"RevistaSans","Inter",sans-serif}
.rv-book{transform:perspective(1800px) rotateY(-14deg) rotateX(4deg);transition:transform .6s ease}
.rv-book:hover{transform:perspective(1800px) rotateY(-6deg) rotateX(2deg)}
.rv-cover{transform-origin:left center;transition:transform .9s cubic-bezier(.6,.05,.3,1),opacity .9s}
.rv-opening .rv-cover{transform:perspective(1800px) rotateY(-165deg);opacity:.2}
.rv-reader{animation:rvIn .6s ease both}
@keyframes rvIn{from{opacity:0;transform:scale(.96)}to{opacity:1;transform:none}}
@media (prefers-reduced-motion:reduce){.rv-book,.rv-book:hover{transform:none;transition:none}.rv-cover{transition:none}.rv-reader{animation:none}}
`;

export default function Revista() {
  const { language } = useI18n();
  const lang = language === "es" ? "es" : "en";
  const t = copy[lang];
  const [state, setState] = useState<"closed" | "opening" | "open">("closed");
  const [isWide, setIsWide] = useState(() => typeof window !== "undefined" && window.matchMedia("(min-width: 1024px)").matches);
  const reduced = typeof window !== "undefined" && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  const [emblaRef, embla] = useEmblaCarousel({ align: "start", slidesToScroll: isWide ? 2 : 1, duration: reduced ? 1 : 25 });
  const [snap, setSnap] = useState(0);

  useEffect(() => {
    const mq = window.matchMedia("(min-width: 1024px)");
    const on = () => setIsWide(mq.matches);
    mq.addEventListener("change", on);
    return () => mq.removeEventListener("change", on);
  }, []);

  useEffect(() => {
    if (!embla) return;
    const on = () => setSnap(embla.selectedScrollSnap());
    embla.on("select", on); on();
    return () => { embla.off("select", on); };
  }, [embla]);

  const openBook = () => {
    if (reduced) return setState("open");
    setState("opening");
    window.setTimeout(() => setState("open"), 850);
  };
  const closeBook = () => { setSnap(0); setState("closed"); };

  const prev = useCallback(() => embla?.scrollPrev(), [embla]);
  const next = useCallback(() => embla?.scrollNext(), [embla]);

  useEffect(() => {
    if (state !== "open") return;
    const onKey = (e: KeyboardEvent) => {
      if (["ArrowRight", "PageDown", " "].includes(e.key)) { e.preventDefault(); next(); }
      else if (["ArrowLeft", "PageUp"].includes(e.key)) { e.preventDefault(); prev(); }
      else if (e.key === "Escape") closeBook();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [state, next, prev]);

  const step = isWide ? 2 : 1;
  const first = Math.min(snap * step + 1, TOTAL);
  const counter = isWide && first < TOTAL ? `${first}–${first + 1} / ${TOTAL}` : `${first} / ${TOTAL}`;

  const cta = (
    <LocalizedLink to="/collection-2027"
      className="rv-sans inline-flex min-h-[44px] items-center justify-center border border-revista-gold px-6 text-sm uppercase tracking-[0.2em] text-revista-gold transition-colors hover:bg-revista-gold hover:text-revista-navy">
      REM7 Collection 2027
    </LocalizedLink>
  );

  return (
    <div className="min-h-screen bg-revista-navy text-revista-ivory">
      <Helmet>
        <html lang={lang} />
        <title>{`REM7 Collection 2027 — ${t.label} | Aureum VII Trading`}</title>
        <meta name="description" content={t.lead} />
        <meta name="robots" content="noindex, nofollow" />
        <meta name="googlebot" content="noindex, nofollow" />
        <style>{fontCss}</style>
      </Helmet>
      <Navigation />
      <main className="overflow-hidden px-4 pb-16 pt-28 sm:pt-36">
        <header className="mx-auto mb-10 max-w-3xl text-center">
          <p className="rv-sans mb-3 text-xs uppercase tracking-[0.35em] text-revista-gold">{t.label}</p>
          <h1 className="rv-serif text-4xl sm:text-6xl">REM7 Collection 2027</h1>
          <p className="rv-sans mx-auto mt-4 max-w-xl text-base text-revista-ivory/75">{t.lead}</p>
        </header>

        {state !== "open" ? (
          <div className={`flex flex-col items-center ${state === "opening" ? "rv-opening" : ""}`}>
            <button type="button" onClick={openBook} aria-label={t.open}
              className="rv-book group relative block w-[min(78vw,420px)] focus:outline-none focus-visible:ring-2 focus-visible:ring-revista-gold">
              {/* page edges */}
              <span aria-hidden className="absolute inset-y-2 -right-3 w-3 rounded-r-sm bg-[repeating-linear-gradient(90deg,hsl(var(--revista-ivory)),hsl(var(--revista-ivory))_1px,hsl(var(--revista-ivory)/0.7)_2px)] shadow-inner" />
              <span aria-hidden className="absolute -bottom-3 left-6 right-0 h-3 rounded-b-sm bg-[repeating-linear-gradient(0deg,hsl(var(--revista-ivory)),hsl(var(--revista-ivory))_1px,hsl(var(--revista-ivory)/0.7)_2px)]" />
              <span className="relative flex shadow-[0_40px_80px_-20px_hsl(0_0%_0%/0.8)]">
                {/* spine */}
                <span aria-hidden className="flex w-8 shrink-0 flex-col items-center justify-center gap-6 rounded-l-sm bg-revista-spine bg-[repeating-linear-gradient(45deg,transparent,transparent_2px,hsl(var(--revista-ivory)/0.04)_2px,hsl(var(--revista-ivory)/0.04)_3px)] sm:w-10">
                  <span className="rv-serif text-sm tracking-[0.3em] text-revista-gold [writing-mode:vertical-rl]">REM7</span>
                  <span className="rv-sans text-[9px] tracking-[0.3em] text-revista-gold/80 [writing-mode:vertical-rl]">COLLECTION 2027</span>
                </span>
                <span className="rv-cover block flex-1 overflow-hidden rounded-r-sm">
                  <img src={pages[0]} alt="REM7 Collection 2027 — cover" width={1080} height={1350} fetchPriority="high" className="block h-auto w-full" />
                </span>
              </span>
            </button>
            <p className="rv-sans mt-10 animate-pulse text-sm uppercase tracking-[0.3em] text-revista-gold motion-reduce:animate-none">{t.open}</p>
          </div>
        ) : (
          <div className="rv-reader mx-auto max-w-6xl">
            <div className="overflow-hidden shadow-[0_40px_80px_-20px_hsl(0_0%_0%/0.8)]" ref={emblaRef} role="region" aria-roledescription="carousel" aria-label="REM7 Collection 2027">
              <div className="flex">
                {pages.map((src, i) => (
                  <div key={src} className="min-w-0 shrink-0 grow-0 basis-full lg:basis-1/2" aria-roledescription="slide" aria-label={`${t.page} ${i + 1} / ${TOTAL}`}>
                    <img src={src} alt={`REM7 Collection 2027 — ${t.page} ${i + 1}`} width={1080} height={1350}
                      loading={i < 3 ? "eager" : "lazy"} decoding="async" className="block h-auto w-full bg-revista-ivory" />
                  </div>
                ))}
              </div>
            </div>
            <div className="rv-sans mt-6 flex items-center justify-between gap-4">
              <button type="button" onClick={prev} aria-label={t.prev} className="flex h-12 w-12 items-center justify-center rounded-full border border-revista-gold/60 text-revista-gold hover:bg-revista-gold/10">
                <ChevronLeft className="h-5 w-5" strokeWidth={1} />
              </button>
              <span aria-live="polite" className="text-sm tracking-[0.2em] text-revista-ivory/80">{counter}</span>
              <button type="button" onClick={next} aria-label={t.next} className="flex h-12 w-12 items-center justify-center rounded-full border border-revista-gold/60 text-revista-gold hover:bg-revista-gold/10">
                <ChevronRight className="h-5 w-5" strokeWidth={1} />
              </button>
            </div>
            <div className="mt-6 flex justify-center">
              <button type="button" onClick={closeBook} className="rv-sans inline-flex min-h-[44px] items-center gap-2 text-sm uppercase tracking-[0.2em] text-revista-ivory/70 hover:text-revista-gold">
                <X className="h-4 w-4" /> {t.close}
              </button>
            </div>
          </div>
        )}

        <div className="mx-auto mt-14 flex max-w-3xl flex-col items-center gap-4 sm:flex-row sm:justify-center">
          {cta}
          <a href="/files/REM7_Collection_2027_Magazine.pdf" download="REM7_Collection_2027_Magazine.pdf"
            className="rv-sans inline-flex min-h-[44px] items-center gap-2 px-4 text-sm uppercase tracking-[0.2em] text-revista-ivory/80 hover:text-revista-gold">
            <Download className="h-4 w-4" /> {t.pdf}
          </a>
        </div>
        <p className="rv-sans mt-10 text-center text-sm text-revista-ivory/60">
          {t.contact}: <a href="tel:+34688495309" className="hover:text-revista-gold">+34 688 495 309</a> ·{" "}
          <a href="mailto:info@aureum7trading.com" className="hover:text-revista-gold">info@aureum7trading.com</a>
        </p>
      </main>
      <Footer hideYear />
    </div>
  );
}
